import type { OptionValue } from '@tempmee/core';
import {
  Box,
  FormField,
  InputNumberField,
  InputSelectField,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { ISignUpFormValues } from '../../types';
import { COST_UNIT_OPTIONS, PARKING_TYPE_FALLBACK, YES_NO_OPTIONS } from '../constants';
import { SectionHeading } from './SectionHeading';

const SECTION_GAP = 32;
const FIELD_GAP = 24;
const COST_PATTERN = /^\d+(\.\d{0,2})?$/;

export const ParkingSection = () => {
  const { control } = useFormContext<ISignUpFormValues>();

  const parkingTypesFromMetadata = useAppSelector(
    (state) => (state.user.allMetadata as { parkingTypes?: string[] } | null)?.parkingTypes,
  );

  const parkingTypeOptions = useMemo<OptionValue<string>[]>(
    () =>
      parkingTypesFromMetadata?.length
        ? parkingTypesFromMetadata.map((t) => ({ label: t, value: t }))
        : PARKING_TYPE_FALLBACK,
    [parkingTypesFromMetadata],
  );

  const isParkingFree = useWatch({ control, name: 'parkingInfo.isFree' });
  const showCostRow = isParkingFree === 'no';

  return (
    <Box style={{ marginTop: SECTION_GAP }}>
      <SectionHeading>What Parking Options Are Available at Your Office?</SectionHeading>
      <Row gap={3} align="start">
        <Box style={{ flex: 2 }}>
          <FormField label="Parking Type" htmlFor="parkingInfo.type" labelWeight="normal">
            <Controller
              name="parkingInfo.type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <div data-cy="select-parking-type">
                  <InputSelectField<string>
                    options={parkingTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    isError={Boolean(fieldState.error)}
                  />
                </div>
              )}
            />
          </FormField>
        </Box>
        <Box style={{ flex: 1 }}>
          <FormField label="Is Parking Free?" htmlFor="parkingInfo.isFree" labelWeight="normal">
            <Controller
              name="parkingInfo.isFree"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <div data-cy="select-parking-is-free">
                  <InputSelectField<string>
                    options={YES_NO_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    isError={Boolean(fieldState.error)}
                  />
                </div>
              )}
            />
          </FormField>
        </Box>
      </Row>
      {showCostRow && (
        <Row gap={3} align="start" style={{ marginTop: FIELD_GAP }}>
          <Box style={{ flex: 2 }}>
            <FormField label="Parking Cost" htmlFor="parkingInfo.cost" labelWeight="normal">
              <Controller
                name="parkingInfo.cost"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <InputNumberField
                    data-cy="input-parking-cost"
                    id="parkingInfo.cost"
                    placeholder="--.--"
                    isError={Boolean(fieldState.error)}
                    leftIcon={
                      <Typography size="body_large" color={colors.neutral[500]}>
                        US$
                      </Typography>
                    }
                    value={field.value}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === '' || COST_PATTERN.test(next)) {
                        field.onChange(next);
                      }
                    }}
                  />
                )}
              />
            </FormField>
          </Box>
          <Box style={{ flex: 1 }}>
            <FormField label="Cost Unit" htmlFor="parkingInfo.unit" labelWeight="normal">
              <Controller
                name="parkingInfo.unit"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <div data-cy="select-parking-unit">
                    <InputSelectField<string>
                      options={COST_UNIT_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select"
                      isError={Boolean(fieldState.error)}
                    />
                  </div>
                )}
              />
            </FormField>
          </Box>
        </Row>
      )}
    </Box>
  );
};
